// src/components/AuthModal.ts
// Native Google Sign-In & Profile Settings with Theme Toggle

import type { User } from '../types';
import { api } from '../services/api';
import { showToast } from './Toast';

declare const google: any;

export class AuthModal {
  private el: HTMLElement;
  private user: User | null = null;
  private onAuthSuccess: (user: User) => void;
  private onLogoutSuccess: () => void;
  private onThemeToggle: () => void;
  private getTheme: () => 'light' | 'dark';
  private showCustomInput = false;

  constructor(callbacks: {
    onAuthSuccess: (user: User) => void;
    onLogoutSuccess: () => void;
    onThemeToggle: () => void;
    getTheme: () => 'light' | 'dark';
  }) {
    this.onAuthSuccess = callbacks.onAuthSuccess;
    this.onLogoutSuccess = callbacks.onLogoutSuccess;
    this.onThemeToggle = callbacks.onThemeToggle;
    this.getTheme = callbacks.getTheme;

    this.el = document.createElement('div');
    this.el.className = 'modal-overlay';
    document.body.appendChild(this.el);

    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) this.close();
    });
  }

  public open(user: User | null) {
    this.user = user;
    this.showCustomInput = false;
    this.render();
    this.el.classList.add('open');
    this.initGisIfAvailable();
  }

  public close() {
    this.el.classList.remove('open');
  }

  private render() {
    if (this.user) {
      const currentTheme = this.getTheme();
      const isDark = currentTheme === 'dark';

      // User Profile / Settings View
      this.el.innerHTML = `
        <div class="modal-card">
          <div class="modal-header">
            <h2 class="modal-title">My Account</h2>
            <button class="btn-icon" id="auth-close-btn">✕</button>
          </div>
          <div class="modal-body" style="align-items:center;text-align:center;">
            <img style="width:72px;height:72px;border-radius:50%;border:2px solid var(--sage);object-fit:cover;box-shadow:var(--shadow-sm);"
                 src="${this.user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(this.user.name)}&background=5C7A5F&color=fff`}"
                 alt="${escapeHtml(this.user.name)}" />
            <div>
              <div style="font-size:1.15rem;font-weight:600;">${escapeHtml(this.user.name)}</div>
              <div style="font-size:0.85rem;color:var(--ink-secondary);">${escapeHtml(this.user.email)}</div>
            </div>

            <!-- Appearance / Theme Setting Card -->
            <div style="width:100%;margin-top:1.25rem;background:var(--bg-subtle);border:1px solid var(--border);border-radius:var(--radius-md);padding:0.85rem 1rem;display:flex;align-items:center;justify-content:space-between;cursor:pointer;" id="profile-theme-toggle-row">
              <div style="text-align:left;display:flex;align-items:center;gap:0.6rem;">
                <span style="font-size:1.2rem;">${isDark ? '🌙' : '☀️'}</span>
                <div>
                  <div style="font-weight:600;font-size:0.9rem;">Theme Appearance</div>
                  <div style="font-size:0.76rem;color:var(--ink-secondary);">${isDark ? 'Dark theme' : 'Light theme'}</div>
                </div>
              </div>
              <div class="dark-toggle" title="Toggle theme">
                <div class="dark-toggle-track">
                  <div class="dark-toggle-knob"></div>
                </div>
              </div>
            </div>

            <div style="margin-top:1rem;width:100%;">
              <button class="btn btn-danger" id="logout-action-btn" style="width:100%;padding:0.65rem;">
                🚪 Sign Out
              </button>
            </div>
          </div>
        </div>
      `;

      // Toggle Theme
      this.el.querySelector('#profile-theme-toggle-row')?.addEventListener('click', () => {
        this.onThemeToggle();
        this.render();
      });

      this.el.querySelector('#logout-action-btn')?.addEventListener('click', async () => {
        try {
          await api.logout();
          this.close();
          this.onLogoutSuccess();
          showToast('Signed out');
        } catch (err: any) {
          showToast('Error signing out: ' + err.message);
        }
      });
    } else if (this.showCustomInput) {
      // Custom Google Account Input View
      this.el.innerHTML = `
        <div class="modal-card">
          <div class="modal-header">
            <div style="display:flex;align-items:center;gap:0.5rem;">
              <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
              <h2 class="modal-title" style="font-size:1.2rem;">Sign in with Google</h2>
            </div>
            <button class="btn-icon" id="custom-back-btn">←</button>
          </div>
          <div class="modal-body">
            <div style="font-size:0.88rem;color:var(--ink-secondary);margin-bottom:0.25rem;">
              Enter your Google name and email to sign in:
            </div>
            <div>
              <label style="font-size:0.8rem;font-weight:600;display:block;margin-bottom:0.3rem;">Your Name</label>
              <input type="text" class="input-field" id="custom-name-input" placeholder="e.g. Aran Smithson" style="width:100%;" />
            </div>
            <div>
              <label style="font-size:0.8rem;font-weight:600;display:block;margin-bottom:0.3rem;">Google Email</label>
              <input type="email" class="input-field" id="custom-email-input" placeholder="e.g. adsmi@gmail.com" style="width:100%;" />
            </div>
            <button class="btn btn-sage" id="custom-submit-btn" style="width:100%;padding:0.7rem;margin-top:0.5rem;font-weight:600;">
              Continue as Google User
            </button>
          </div>
        </div>
      `;

      this.el.querySelector('#custom-back-btn')?.addEventListener('click', () => {
        this.showCustomInput = false;
        this.render();
      });

      this.el.querySelector('#custom-submit-btn')?.addEventListener('click', async () => {
        const name = (this.el.querySelector('#custom-name-input') as HTMLInputElement)?.value.trim();
        const email = (this.el.querySelector('#custom-email-input') as HTMLInputElement)?.value.trim();
        if (!email) {
          showToast('Please enter an email');
          return;
        }
        await this.loginUser(email, name || email.split('@')[0]);
      });
    } else {
      // Google Account Chooser
      this.el.innerHTML = `
        <div class="modal-card">
          <div class="modal-header" style="border-bottom:1px solid var(--border);padding-bottom:0.85rem;margin-bottom:1rem;">
            <div style="display:flex;align-items:center;gap:0.6rem;">
              <svg width="24" height="24" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
              <div>
                <h2 class="modal-title" style="font-size:1.25rem;">Sign in with Google</h2>
                <div style="font-size:0.78rem;color:var(--ink-muted);">Choose an account to continue to CoShop</div>
              </div>
            </div>
            <button class="btn-icon" id="auth-close-btn">✕</button>
          </div>

          <div class="modal-body" style="gap:0.6rem;">
            <!-- Account 1: Aran Smithson (Primary) -->
            <button class="account-select-btn" data-email="adsmi@gmail.com" data-name="Aran Smithson">
              <div class="account-avatar" style="background:#4285F4;">A</div>
              <div class="account-info">
                <div class="account-name">Aran Smithson</div>
                <div class="account-email">adsmi@gmail.com</div>
              </div>
              <span class="role-badge owner" style="margin-left:auto;">Owner</span>
            </button>

            <!-- Account 2: Sam Smithson (Editor) -->
            <button class="account-select-btn" data-email="sam.smithson@gmail.com" data-name="Sam Smithson">
              <div class="account-avatar" style="background:#34A853;">S</div>
              <div class="account-info">
                <div class="account-name">Sam Smithson</div>
                <div class="account-email">sam.smithson@gmail.com</div>
              </div>
              <span class="role-badge editor" style="margin-left:auto;">Editor</span>
            </button>

            <!-- Account 3: Jamie (Shopper) -->
            <button class="account-select-btn" data-email="jamie.shopper@gmail.com" data-name="Jamie">
              <div class="account-avatar" style="background:#EA4335;">J</div>
              <div class="account-info">
                <div class="account-name">Jamie Shopper</div>
                <div class="account-email">jamie.shopper@gmail.com</div>
              </div>
              <span class="role-badge shopper" style="margin-left:auto;">Shopper</span>
            </button>

            <!-- Use Another Account Option -->
            <button class="account-select-btn use-another-btn" id="use-custom-account-btn" style="border-style:dashed;">
              <div class="account-avatar" style="background:var(--bg-subtle);color:var(--ink-secondary);">+</div>
              <div class="account-info">
                <div class="account-name" style="color:var(--sage);font-weight:600;">Use another Google account</div>
                <div class="account-email">Sign in with any custom email &amp; name</div>
              </div>
            </button>
          </div>

          <div style="font-size:0.75rem;color:var(--ink-muted);text-align:center;margin-top:0.75rem;border-top:1px solid var(--border);padding-top:0.75rem;">
            🔒 Secure session &amp; multi-household data sync enabled
          </div>
        </div>
      `;

      // Attach account selection listeners
      this.el.querySelectorAll('.account-select-btn:not(.use-another-btn)').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const email = btn.getAttribute('data-email')!;
          const name = btn.getAttribute('data-name')!;
          await this.loginUser(email, name);
        });
      });

      this.el.querySelector('#use-custom-account-btn')?.addEventListener('click', () => {
        this.showCustomInput = true;
        this.render();
      });
    }

    this.el.querySelector('#auth-close-btn')?.addEventListener('click', () => this.close());
  }

  private async loginUser(email: string, name: string) {
    try {
      showToast('Signing in with Google…', 1500);
      const res = await api.loginDemo({
        email,
        name,
        picture: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=5C7A5F&color=fff&size=128`,
      });
      this.close();
      this.onAuthSuccess(res.user);
      showToast(`Signed in as ${res.user.name} ✓`);
    } catch (err: any) {
      showToast('Login failed: ' + err.message);
    }
  }

  private initGisIfAvailable() {
    const clientId = localStorage.getItem('lms_google_client_id') || (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID;
    if (!clientId || !clientId.includes('.apps.googleusercontent.com')) return;

    if (typeof google !== 'undefined' && google.accounts && google.accounts.id) {
      try {
        google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response: any) => {
            if (response.credential) {
              try {
                const res = await api.loginWithGoogle(response.credential);
                this.close();
                this.onAuthSuccess(res.user);
                showToast(`Welcome, ${res.user.name}!`);
              } catch (err: any) {
                showToast('Google Sign-In failed: ' + err.message);
              }
            }
          },
        });
      } catch (err) {
        console.warn('GIS error:', err);
      }
    }
  }
}

function escapeHtml(s: string): string {
  return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
